/* iter178 — Marketing Lists tab inside Communication Settings.
   Read-only view of master + per-campaign promo contact lists with
   member/consent/opt-out counts and CSV export. No mass-text send yet.
*/
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';
import { Users, Download, Star, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MarketingListsTab = () => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // { id, name, members }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/marketing-lists`, { withCredentials: true });
      setLists(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load marketing lists');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (list) => {
    try {
      const { data } = await axios.get(`${API}/admin/marketing-lists/${list.id}`, { withCredentials: true });
      setDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load members');
    }
  };

  const exportCsv = async (list) => {
    try {
      const res = await axios.get(`${API}/admin/marketing-lists/${list.id}/export.csv`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(list.name || 'list').replace(/[^a-z0-9_-]+/gi, '_')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  if (loading) {
    return <p data-testid="marketing-lists-loading" className="text-[#A3A3A3]">Loading…</p>;
  }

  return (
    <div data-testid="marketing-lists-tab" className="space-y-4">
      <div>
        <h3 className="font-['Outfit'] text-xl font-semibold text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-[#D4AF37]" /> Marketing Lists
        </h3>
        <p className="text-[#A3A3A3] text-sm mt-1 max-w-3xl">
          Promo intake submissions deduplicate into the master list (one row per person)
          and also land in the per-campaign list. Mass-text sending is intentionally not enabled yet —
          this tab is read-only with CSV export so you can plan, audit, or use the contacts in another tool.
        </p>
      </div>

      {lists.length === 0 ? (
        <p data-testid="marketing-lists-empty" className="text-[#525252] italic">No lists yet.</p>
      ) : (
        <div className="space-y-2">
          {lists.map((L) => (
            <div
              key={L.id}
              data-testid={`marketing-list-row-${L.id}`}
              className="bg-[#141414] border border-[#262626] rounded-md p-4 flex flex-wrap items-center gap-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {L.kind === 'master' ? (
                    <Star className="w-4 h-4 text-[#D4AF37]" />
                  ) : (
                    <Tag className="w-4 h-4 text-[#A3A3A3]" />
                  )}
                  <p className="text-white font-bold">{L.name}</p>
                  <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3] bg-[#0A0A0A] border border-[#262626] px-1.5 py-0.5 rounded">
                    {L.kind === 'master' ? 'Master' : 'Campaign'}
                  </span>
                </div>
                <p className="text-[#737373] text-xs mt-1">
                  <span data-testid={`list-member-count-${L.id}`}>{L.member_count} members</span>
                  {' · '}
                  <span className="text-green-300" data-testid={`list-consent-count-${L.id}`}>{L.consent_count} consented & textable</span>
                  {' · '}
                  <span
                    className={L.opt_out_count > 0 ? "text-red-300" : "text-[#525252]"}
                    data-testid={`list-opt-out-count-${L.id}`}
                  >
                    {L.opt_out_count} opted out
                  </span>
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  data-testid={`marketing-list-view-${L.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => openDetail(L)}
                  className="border-[#262626] text-white hover:bg-[#262626]"
                >
                  View
                </Button>
                <Button
                  data-testid={`marketing-list-export-${L.id}`}
                  size="sm"
                  onClick={() => exportCsv(L)}
                  className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"
                >
                  <Download className="w-4 h-4 mr-1" /> CSV
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <SheetContent
          side="right"
          className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-2xl overflow-y-auto"
          data-testid="marketing-list-detail"
        >
          <SheetHeader>
            <SheetTitle className="text-white">{detail?.name}</SheetTitle>
            <SheetDescription className="text-[#A3A3A3]">
              {detail?.members?.length || 0} members. Opt-out is shown for transparency — mass texting is not enabled yet.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-5 space-y-2">
            {(detail?.members || []).length === 0 ? (
              <p className="text-[#525252] italic text-sm">Nobody on this list yet.</p>
            ) : (
              (detail?.members || []).map((m) => (
                <div key={m.id} className="bg-[#0A0A0A] border border-[#262626] rounded p-3 text-xs">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-white font-bold">{[m.first_name, m.last_name].filter(Boolean).join(' ') || '—'}</p>
                    <div className="flex items-center gap-1">
                      {m.consent ? (
                        <span className="text-[10px] uppercase tracking-wider text-green-300 bg-green-500/15 border border-green-500/40 px-1.5 py-0.5 rounded">Consent</span>
                      ) : null}
                      {m.sms_opt_out ? (
                        <span className="text-[10px] uppercase tracking-wider text-red-300 bg-red-500/15 border border-red-500/40 px-1.5 py-0.5 rounded">STOP</span>
                      ) : null}
                    </div>
                  </div>
                  <p className="text-[#A3A3A3] mt-1">{m.phone} {m.email && <>· {m.email}</>}</p>
                  <p className="text-[#737373] truncate mt-0.5">{[m.address, m.city, m.zip].filter(Boolean).join(', ')}</p>
                  <p className="text-[#525252] mt-1">Joined: {m.joined_at ? new Date(m.joined_at).toLocaleString() : '—'} · source: {m.source_promo || '—'}</p>
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default MarketingListsTab;
